import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useMemo, useRef } from "react";
import type { Coordinate, Locale, OperationsOrder } from "./domain";

const MAP_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
      maxzoom: 19,
    },
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
};

const TUNIS_BOUNDS: maplibregl.LngLatBoundsLike = [
  [10.1, 36.75],
  [10.35, 36.9],
];

type Tone = "amber" | "blue" | "green" | "grey" | "ink" | "red";

interface LiveMapPoint {
  key: string;
  label: string;
  latitude: number;
  longitude: number;
  orderId: string | null;
  tone: Tone;
}

interface LiveMapProps {
  locale: Locale;
  online: boolean;
  onSelect: (id: string) => void;
  orderDetail: Record<string, unknown> | null;
  orders: OperationsOrder[];
  selectedId: string | null;
}

export function LiveMap({
  locale,
  online,
  onSelect,
  orderDetail,
  orders,
  selectedId,
}: LiveMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef(new globalThis.Map<string, maplibregl.Marker>());
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const points = useMemo(
    () => collectPoints(orders, orderDetail, selectedId, locale),
    [orders, orderDetail, selectedId, locale],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container,
        style: MAP_STYLE,
        center: [10.18, 36.82],
        zoom: 11,
        attributionControl: { compact: true },
      });
    } catch {
      return;
    }
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
    map.fitBounds(TUNIS_BOUNDS, { duration: 0, padding: 24 });
    mapRef.current = map;
    for (const marker of markersRef.current.values()) marker.addTo(map);
    return () => {
      for (const marker of markersRef.current.values()) marker.remove();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const markers = markersRef.current;
    const wanted = new globalThis.Map(points.map((point) => [point.key, point]));
    for (const [key, marker] of markers) {
      if (!wanted.has(key)) {
        marker.remove();
        markers.delete(key);
      }
    }
    for (const point of wanted.values()) {
      let marker = markers.get(point.key);
      if (!marker) {
        const element = document.createElement("button");
        element.type = "button";
        element.addEventListener("click", () => {
          if (point.orderId) onSelectRef.current(point.orderId);
        });
        marker = new maplibregl.Marker({ element });
        markers.set(point.key, marker);
        marker.setLngLat([point.longitude, point.latitude]).addTo(map);
      } else {
        marker.setLngLat([point.longitude, point.latitude]);
      }
      const element = marker.getElement();
      element.className = `live-marker tone-${point.tone}${point.selected ? " selected" : ""}`;
      element.setAttribute("aria-label", point.label);
      element.title = point.label;
    }
  }, [points]);

  const recenter = () => {
    const map = mapRef.current;
    if (!map) return;
    if (points.length === 0) {
      map.fitBounds(TUNIS_BOUNDS, { padding: 24 });
      return;
    }
    let west = Number.POSITIVE_INFINITY;
    let south = Number.POSITIVE_INFINITY;
    let east = Number.NEGATIVE_INFINITY;
    let north = Number.NEGATIVE_INFINITY;
    for (const point of points) {
      west = Math.min(west, point.longitude);
      south = Math.min(south, point.latitude);
      east = Math.max(east, point.longitude);
      north = Math.max(north, point.latitude);
    }
    map.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { maxZoom: 14, padding: 48 },
    );
  };

  return (
    <section className="ops-map surface">
      <div className="map-label">
        <span className="section-kicker">TUNIS LIVE MAP</span>
        <b>
          {points.length} {locale === "ar" ? "نقطة نشطة" : "points actifs"}
        </b>
      </div>
      <div
        aria-label={locale === "ar" ? "خريطة الطلبات النشطة" : "Carte des commandes actives"}
        className="ops-map-canvas"
        ref={containerRef}
        role="img"
      />
      {points.length === 0 && (
        <div className="map-overlay">
          <b>{locale === "ar" ? "لا توجد نقاط نشطة" : "Aucun point actif"}</b>
        </div>
      )}
      {!online && (
        <div className="map-overlay offline">
          <b>
            {locale === "ar"
              ? "الاتصال منقطع — تعرض الخريطة آخر بيانات متاحة"
              : "Hors ligne — dernières données disponibles"}
          </b>
        </div>
      )}
      <div className="map-foot">
        <span>
          <i className="dot green" />
          {locale === "ar" ? "يتحرك" : "En mouvement"}
        </span>
        <span>
          <i className="dot amber" />
          {locale === "ar" ? "يحتاج انتباه" : "À surveiller"}
        </span>
        <span>
          <i className="dot blue" />
          {locale === "ar" ? "كابتن" : "Coursier"}
        </span>
        <button onClick={recenter} type="button">
          {locale === "ar" ? "توسيط الخريطة" : "Recentrer"}
        </button>
      </div>
    </section>
  );
}

function collectPoints(
  orders: OperationsOrder[],
  orderDetail: Record<string, unknown> | null,
  selectedId: string | null,
  locale: Locale,
): Array<LiveMapPoint & { selected: boolean }> {
  const result: Array<LiveMapPoint & { selected: boolean }> = [];
  for (const order of orders) {
    const { latitude, longitude } = order.destination;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    result.push({
      key: `order-${order.id}`,
      label: `${locale === "ar" ? "فتح الطلب" : "Ouvrir la commande"} ${order.publicReference}`,
      latitude,
      longitude,
      orderId: order.id,
      selected: order.id === selectedId,
      tone: statusTone(order.status),
    });
  }
  const courierLocation = detailCoordinate(orderDetail, "courier", "location");
  if (courierLocation) {
    const name = detailText(orderDetail, "courier", "name");
    result.push({
      key: "courier",
      label: `${locale === "ar" ? "موقع الكابتن" : "Position du coursier"}${name ? ` — ${name}` : ""}`,
      latitude: courierLocation.latitude,
      longitude: courierLocation.longitude,
      orderId: null,
      selected: false,
      tone: "blue",
    });
  }
  const storeLocation =
    detailCoordinate(orderDetail, "store", "location") ?? detailCoordinate(orderDetail, "pickup");
  if (storeLocation) {
    const name =
      detailText(orderDetail, "store", "name") ?? detailText(orderDetail, "merchant", "name");
    result.push({
      key: "store",
      label: `${locale === "ar" ? "نقطة الالتقاط" : "Point de collecte"}${name ? ` — ${name}` : ""}`,
      latitude: storeLocation.latitude,
      longitude: storeLocation.longitude,
      orderId: null,
      selected: false,
      tone: "ink",
    });
  }
  return result;
}

function statusTone(status: string): Tone {
  if (status === "picked_up") return "green";
  if (status === "cancelled" || status === "refunded") return "red";
  if (status === "delivered") return "grey";
  return "amber";
}

function detailCoordinate(
  detail: Record<string, unknown> | null,
  ...path: string[]
): Coordinate | null {
  let current: unknown = detail;
  for (const key of path) {
    if (typeof current !== "object" || current === null) return null;
    current = (current as Record<string, unknown>)[key];
  }
  if (typeof current !== "object" || current === null) return null;
  const record = current as Record<string, unknown>;
  const latitude = Number(record.latitude);
  const longitude = Number(record.longitude);
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null;
}

function detailText(detail: Record<string, unknown> | null, ...path: string[]): string | null {
  let current: unknown = detail;
  for (const key of path) {
    if (typeof current !== "object" || current === null) return null;
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === "string" && current.trim() ? current : null;
}
